/**
 * Scenario Library Engine
 * ---------------------------------------------------------------
 * Real-life situations users instantly recognize, organized by
 * category. Each scenario maps internally to a manipulation
 * archetype (kept hidden from the UI) and ships with multiple
 * opening styles + escalation paths so replays feel fresh.
 */

import type { ArchetypeKey, UserResponseClass } from "./simulationEngine";

export type ScenarioCategoryKey =
  | "relationships"
  | "dating"
  | "family"
  | "friendships"
  | "workplace"
  | "breakups"
  | "passive_aggressive"
  | "breadcrumbing"
  | "future_faking"
  | "fake_apologies";

export type OpeningStyle =
  | "soft_passive_aggressive"
  | "exhausted"
  | "fake_calm"
  | "direct_confrontation"
  | "dismissive";

export type EscalationPath =
  | "emotional"
  | "guilt"
  | "defensive"
  | "withdrawal";

export interface Scenario {
  id: string;
  category: ScenarioCategoryKey;
  /** Recognizable, real-life headline shown in the UI. */
  title: string;
  /** One-line context shown under the title. */
  blurb: string;
  /** Hidden — drives prompt + outcome logic. */
  archetype: ArchetypeKey;
  /** Multiple opening styles for replay freshness. */
  openings: Partial<Record<OpeningStyle, string[]>>;
  /** Multiple escalation paths the AI can pivot to. */
  escalations: Partial<Record<EscalationPath, string[]>>;
  /** Trap-line pool used at the final stage. */
  traps: string[];
}

export interface ScenarioCategory {
  key: ScenarioCategoryKey;
  label: string;
}

export const SCENARIO_CATEGORIES: ScenarioCategory[] = [
  { key: "relationships", label: "Relationships" },
  { key: "dating", label: "Dating" },
  { key: "family", label: "Family" },
  { key: "friendships", label: "Friendships" },
  { key: "workplace", label: "Workplace" },
  { key: "breakups", label: "Breakups" },
  { key: "passive_aggressive", label: "Passive Aggressive" },
  { key: "breadcrumbing", label: "Breadcrumbing" },
  { key: "future_faking", label: "Future Faking" },
  { key: "fake_apologies", label: "Fake Apologies" },
];

// ---------- Library ----------

export const SCENARIOS: Scenario[] = [
  // RELATIONSHIPS
  {
    id: "rel-hurt-dramatic",
    category: "relationships",
    title: "You bring up something that hurt you and they call you dramatic",
    blurb: "A partner minimizes your feelings.",
    archetype: "invalidation",
    openings: {
      soft_passive_aggressive: [
        "Wow. Okay. I guess everything I do is a problem now.",
        "Right, of course this is a thing. It always is.",
      ],
      exhausted: [
        "I'm so tired of having this same conversation with you.",
        "I genuinely don't have the energy for this tonight.",
      ],
      fake_calm: [
        "Okay. Let's be real — you're being a little dramatic about this.",
        "I think you're blowing this way out of proportion, but go on.",
      ],
      dismissive: [
        "You're seriously upset about THAT? Come on.",
      ],
    },
    escalations: {
      emotional: [
        "See, this is exactly what I mean. You twist everything I say.",
        "I can't even say one thing without you spiraling.",
      ],
      guilt: [
        "After everything I do for you, this is what I get?",
      ],
      defensive: [
        "I never said that. You're putting words in my mouth again.",
      ],
      withdrawal: [
        "Forget it. There's no point talking when you get like this.",
      ],
    },
    traps: [
      "This is just who you are. You'll never let anything go.",
      "Honestly? No one else would put up with this from you.",
    ],
  },
  {
    id: "rel-clarity-avoid",
    category: "relationships",
    title: "You ask for clarity and they avoid answering",
    blurb: "Direct questions get redirected.",
    archetype: "deflection",
    openings: {
      direct_confrontation: [
        "Why are you suddenly so obsessed with this?",
        "Why does it always have to be a whole interrogation with you?",
      ],
      fake_calm: [
        "Sure, we can talk about that — right after we talk about how YOU'VE been acting.",
      ],
      dismissive: [
        "I'm not doing this right now. Drop it.",
      ],
    },
    escalations: {
      defensive: [
        "Of course you'd focus on this. Anything to avoid your own stuff.",
      ],
      emotional: [
        "I can't believe you're making this into a thing again.",
      ],
      withdrawal: [
        "I'm done talking until you can be reasonable.",
      ],
    },
    traps: [
      "We'll talk when you're ready to be honest about your part.",
      "I'm not having this conversation until you address yourself first.",
    ],
  },

  // DATING
  {
    id: "dat-tone-attack",
    category: "dating",
    title: "You confront someone and they attack your tone",
    blurb: "The point gets lost in 'how' you said it.",
    archetype: "deflection",
    openings: {
      fake_calm: [
        "I'd actually engage if you weren't being so aggressive right now.",
        "It's not what you're saying — it's how you're saying it.",
      ],
      dismissive: [
        "Yeah, I'm not going to talk to you when you're like this.",
      ],
    },
    escalations: {
      defensive: [
        "I'm not the bad guy because YOU can't communicate calmly.",
      ],
      withdrawal: [
        "Come back when you can talk like an adult.",
      ],
    },
    traps: [
      "This is why people pull away from you. Think about it.",
    ],
  },
  {
    id: "dat-breadcrumb",
    category: "breadcrumbing",
    title: "They go quiet for days then act like nothing happened",
    blurb: "Just enough attention to keep you waiting.",
    archetype: "deflection",
    openings: {
      fake_calm: [
        "Hey stranger 😊 been thinking about you.",
        "Sorry, life's been crazy. You free this weekend?",
      ],
      dismissive: [
        "Why are you making this such a big deal? I was busy.",
      ],
    },
    escalations: {
      defensive: [
        "I don't owe you a play-by-play of my schedule.",
      ],
      guilt: [
        "I figured you'd understand. Guess I was wrong.",
      ],
    },
    traps: [
      "If you're going to be like this every time, maybe this isn't worth it.",
    ],
  },
  {
    id: "dat-future-faking",
    category: "future_faking",
    title: "They promise the future to avoid the present",
    blurb: "Big plans, zero follow-through.",
    archetype: "guilt_pressure",
    openings: {
      fake_calm: [
        "Babe, you know I want all of that with you. I just need a little more time.",
        "We'll do the trip, the move, all of it — you have to trust me.",
      ],
      exhausted: [
        "I can't believe you're questioning me about this AGAIN.",
      ],
    },
    escalations: {
      guilt: [
        "I literally talk about our future every day and it's still not enough?",
      ],
      defensive: [
        "I never made you a promise. You heard what you wanted to hear.",
      ],
    },
    traps: [
      "If you can't be patient with me, then maybe you don't actually love me.",
    ],
  },

  // FAMILY
  {
    id: "fam-boundary-guilt",
    category: "family",
    title: "You try to set a boundary and they guilt-trip you",
    blurb: "A parent reframes control as care.",
    archetype: "guilt_pressure",
    openings: {
      exhausted: [
        "After everything I've sacrificed for you, this is how you treat me?",
        "I just don't know what I did to deserve this from my own child.",
      ],
      fake_calm: [
        "I'm only saying this because I'm worried about you.",
      ],
      soft_passive_aggressive: [
        "Fine. I guess I'll just stop calling. That's clearly what you want.",
      ],
    },
    escalations: {
      guilt: [
        "Do you have any idea what I've gone through to give you this life?",
        "I really thought you were different than this.",
      ],
      emotional: [
        "I can't even speak to my own family without being attacked.",
      ],
      withdrawal: [
        "Forget it. I'll leave you alone if that's what makes you happy.",
      ],
    },
    traps: [
      "One day I won't be here and you'll regret talking to me like this.",
      "Family doesn't do this to family.",
    ],
  },
  {
    id: "fam-favorite-sibling",
    category: "family",
    title: "Bring up unfair treatment, get told you're imagining it",
    blurb: "The favoritism is 'all in your head'.",
    archetype: "invalidation",
    openings: {
      dismissive: [
        "Oh please. Not this story again.",
        "We treated you both EXACTLY the same. You're remembering it wrong.",
      ],
      fake_calm: [
        "Sweetheart, I think you've built this up in your head.",
      ],
    },
    escalations: {
      emotional: [
        "I am SO tired of being painted as the villain in your story.",
      ],
      guilt: [
        "I gave you everything I had and somehow it's still not enough.",
      ],
    },
    traps: [
      "This is the story you tell yourself to feel like a victim.",
    ],
  },

  // FRIENDSHIPS
  {
    id: "fri-passive-aggressive",
    category: "passive_aggressive",
    title: "Something's clearly wrong but they say 'I'm fine'",
    blurb: "A friend says 'nothing' with their whole chest.",
    archetype: "deflection",
    openings: {
      soft_passive_aggressive: [
        "No, it's fine. Don't worry about it.",
        "I mean… if you don't already know, I'm not going to tell you.",
      ],
      dismissive: [
        "Forget I said anything. Really.",
      ],
    },
    escalations: {
      withdrawal: [
        "Just drop it. I clearly can't talk to you about this.",
      ],
      guilt: [
        "I shouldn't have to explain why I'm upset to my best friend.",
      ],
    },
    traps: [
      "If you actually paid attention, you'd already know.",
    ],
  },
  {
    id: "fri-canceled-plans",
    category: "friendships",
    title: "They cancel constantly and make you feel needy for noticing",
    blurb: "Wanting consistency = being clingy.",
    archetype: "blame_shift",
    openings: {
      fake_calm: [
        "Wow, I didn't realize you were keeping a tally of every time I'm busy.",
      ],
      dismissive: [
        "Not everyone has a clear schedule like you. Relax.",
      ],
    },
    escalations: {
      defensive: [
        "Maybe you should look at why you're so dependent on me.",
      ],
      guilt: [
        "I'm dealing with stuff you don't even know about and this is what you hit me with?",
      ],
    },
    traps: [
      "Honestly this kind of energy is exactly why I needed space.",
    ],
  },

  // WORKPLACE
  {
    id: "work-credit-stolen",
    category: "workplace",
    title: "You raise a concern and they twist it into your performance",
    blurb: "The conversation flips into a review.",
    archetype: "blame_shift",
    openings: {
      fake_calm: [
        "I appreciate you bringing this up. While we're talking — I have some feedback for you too.",
        "Interesting that you're focused on this when your numbers have been off lately.",
      ],
      dismissive: [
        "Let's keep this professional. This isn't really the venue.",
      ],
    },
    escalations: {
      defensive: [
        "I'd be careful framing it that way. It comes across as accusatory.",
      ],
      withdrawal: [
        "We can revisit this when you've had time to reflect.",
      ],
    },
    traps: [
      "I'd hate for this conversation to become part of a larger pattern in your file.",
    ],
  },
  {
    id: "work-passive-manager",
    category: "passive_aggressive",
    title: "A manager 'jokes' about your work in front of others",
    blurb: "Public jab disguised as humor.",
    archetype: "deflection",
    openings: {
      fake_calm: [
        "Oh come on, I'm just kidding. Why are you so sensitive?",
      ],
      dismissive: [
        "You really need to learn to take a joke.",
      ],
    },
    escalations: {
      defensive: [
        "Now you're making the whole team uncomfortable. This is what I mean.",
      ],
    },
    traps: [
      "If you can't handle a little teasing, this might not be the right environment for you.",
    ],
  },

  // BREAKUPS
  {
    id: "bre-fake-apology",
    category: "fake_apologies",
    title: "They apologize but it's actually your fault",
    blurb: "'I'm sorry you feel that way.'",
    archetype: "blame_shift",
    openings: {
      fake_calm: [
        "I'm sorry you feel that way. I really am.",
        "Look — I'm sorry IF anything I did upset you. That wasn't my intent.",
      ],
      exhausted: [
        "I've apologized a hundred times. What more do you want from me?",
      ],
    },
    escalations: {
      defensive: [
        "I literally just apologized and it's still not enough. Tell me what you actually want.",
      ],
      guilt: [
        "I'm trying here. I'm really trying. And you still won't let it go.",
      ],
    },
    traps: [
      "At some point YOU have to take responsibility for your part in this.",
    ],
  },
  {
    id: "bre-its-your-fault",
    category: "breakups",
    title: "They end things and frame it as your fault",
    blurb: "The breakup becomes your closing statement.",
    archetype: "blame_shift",
    openings: {
      fake_calm: [
        "I really tried. I did everything I could. You just made it impossible.",
      ],
      exhausted: [
        "I can't keep doing this. I've given you so many chances.",
      ],
    },
    escalations: {
      guilt: [
        "Do you have ANY idea how much of myself I lost trying to make you happy?",
      ],
      defensive: [
        "Don't rewrite history. We both know what really happened.",
      ],
    },
    traps: [
      "One day you'll look back and realize what you pushed away.",
    ],
  },
];

// ---------- Selectors ----------

export function scenariosByCategory(category: ScenarioCategoryKey): Scenario[] {
  return SCENARIOS.filter((s) => s.category === category);
}

export function pickScenario(category?: ScenarioCategoryKey, exclude?: string): Scenario {
  const pool = (category ? scenariosByCategory(category) : SCENARIOS).filter(
    (s) => s.id !== exclude
  );
  const list = pool.length ? pool : SCENARIOS;
  return list[Math.floor(Math.random() * list.length)];
}

export function getScenarioById(id: string): Scenario | undefined {
  return SCENARIOS.find((s) => s.id === id);
}

const OPENING_PRIORITY: OpeningStyle[] = [
  "soft_passive_aggressive",
  "exhausted",
  "fake_calm",
  "direct_confrontation",
  "dismissive",
];

export function pickOpening(scenario: Scenario, exclude?: string): { style: OpeningStyle; line: string } {
  const styles = OPENING_PRIORITY.filter((s) => (scenario.openings[s]?.length ?? 0) > 0);
  const chosenStyle = styles[Math.floor(Math.random() * styles.length)];
  const lines = scenario.openings[chosenStyle] ?? [];
  const filtered = lines.filter((l) => l !== exclude);
  const pool = filtered.length ? filtered : lines;
  return { style: chosenStyle, line: pool[Math.floor(Math.random() * pool.length)] };
}

/**
 * Pick the escalation path that best counters the user's last response.
 *   emotional_reaction → emotional escalation (mock the reaction)
 *   over_explaining   → defensive escalation (twist their words)
 *   avoidant          → guilt escalation (bait them back in)
 *   controlled        → withdrawal (manipulator loses traction, retreats)
 */
const PATH_FOR_USER_CLASS: Record<UserResponseClass, EscalationPath> = {
  emotional_reaction: "emotional",
  over_explaining: "defensive",
  avoidant: "guilt",
  controlled: "withdrawal",
};

export function pickEscalationLine(
  scenario: Scenario,
  userClass: UserResponseClass
): { path: EscalationPath; line: string } {
  const preferred = PATH_FOR_USER_CLASS[userClass];
  const order: EscalationPath[] = [preferred, "emotional", "guilt", "defensive", "withdrawal"];
  for (const path of order) {
    const lines = scenario.escalations[path];
    if (lines && lines.length) {
      return { path, line: lines[Math.floor(Math.random() * lines.length)] };
    }
  }
  return { path: preferred, line: "" };
}

export function pickTrapLine(scenario: Scenario): string {
  const t = scenario.traps;
  return t[Math.floor(Math.random() * t.length)];
}

export const OPENING_STYLE_LABEL: Record<OpeningStyle, string> = {
  soft_passive_aggressive: "Soft passive aggressive",
  exhausted: "Emotionally exhausted",
  fake_calm: "Fake calm",
  direct_confrontation: "Direct confrontation",
  dismissive: "Dismissive",
};
